"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ConsolidatedReportsTable from "../../../components/ConsolidatedReportsTable/ConsolidatedReportsTable";
import ActiveProject from "../../../assets/icons/Region.svg";
import { Select } from "antd";
import {
  getOperatingPerformanceByUnit,
  getNotesToOperatingByUnit,
  getOperatingPersonalPaymentByUnit,
  getOperatingMiscExpensesByUnit,
} from "../../../redux/adminUnitReportSlice/adminUnitReportSlice";
import CustomTab from "../../../components/Common/CustomTab/CustomTab";

// const DEFAULT_FY = "2025-2026";
const getCurrentFY = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0 = Jan

  const fyStartYear = month >= 3 ? year : year - 1; // April-March FY

  return `${fyStartYear}-${fyStartYear + 1}`;
};

const DEFAULT_FY = getCurrentFY();

const tabData = [
  { label: "Operating Performance" },
  { label: "Notes to Operating" },
  { label: "Personnel Payments" },
  { label: "Miscellaneous Expenses" },
];

const quarterOptions = [
  { label: "Quarter 1 (Apr - Jun)", value: "Q1" },
  { label: "Quarter 2 (Jul - Sep)", value: "Q2" },
  { label: "Quarter 3 (Oct - Dec)", value: "Q3" },
  { label: "Quarter 4 (Jan - Mar)", value: "Q4" },
];

const reportTypeOptions = [
  { label: "For", value: "For" },
  { label: "Upto", value: "Upto" },
];

const getStartYearFromFY = (fy) => Number(fy?.split("-")[0]);

/* ======================================================
✅ COMMON REMOVE DESCRIPTION FILTER (GLOBAL)
====================================================== */
const filterRemovedDescriptions = (rows = []) => {
  const REMOVE_DESCRIPTIONS = new Set([
    "Value Added",
    "Value Added as a % of Revenue",
  ]);

  return (Array.isArray(rows) ? rows : []).filter(
    (row) => !REMOVE_DESCRIPTIONS.has(String(row?.DESCRIPTION || "").trim())
  );
};

// ✅ Utility: detect percentage
const isPercentage = (val) => {
  if (val === null || val === undefined) return false;
  return val.toString().includes("%");
};

// ✅ Utility: extract numeric value safely
const getNumericValue = (val) => {
  if (!val) return 0;
  return Number(val.toString().replace(/,/g, "").replace("%", ""));
};

const OperatingPerformanceQuarterWiseReport = () => {
  const dispatch = useDispatch();

  const {
    operatingPerformanceByUnit,
    operatingPersonalPaymentByUnit,
    operatingMiscExpensesByUnit,
    notesToOperatingByUnit,
    loading,
  } = useSelector((state) => state.unitReport);

  const [activeTab, setActiveTab] = useState("0");
  const [selectedFY, setSelectedFY] = useState(DEFAULT_FY);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [selectedReportType, setSelectedReportType] = useState("For");

  const selectedFYStartYear = useMemo(
    () => getStartYearFromFY(selectedFY),
    [selectedFY]
  );

  /* ================= API CALL ================= */
 useEffect(() => {
  if (!selectedFYStartYear) return;

  let from_year, to_year, from_month, to_month;

  const isUpto = selectedReportType === "Upto";

  // ✅ If no quarter selected → full FY
  if (!selectedQuarter) {
    from_year = selectedFYStartYear;
    from_month = "04";
    to_year = selectedFYStartYear + 1;
    to_month = "03";
  } else {
    // ✅ Always fix from when report type is Upto
    if (isUpto) {
      from_year = selectedFYStartYear;
      from_month = "04";
    }

    switch (selectedQuarter) {
      case "Q1":
        if (!isUpto) {
          from_year = selectedFYStartYear;
          from_month = "04";
        }
        to_year = selectedFYStartYear;
        to_month = "06";
        break;

      case "Q2":
        if (!isUpto) {
          from_year = selectedFYStartYear;
          from_month = "07";
        }
        to_year = selectedFYStartYear;
        to_month = "09";
        break;

      case "Q3":
        if (!isUpto) {
          from_year = selectedFYStartYear;
          from_month = "10";
        }
        to_year = selectedFYStartYear;
        to_month = "12";
        break;

      case "Q4":
        if (isUpto) {
          to_year = selectedFYStartYear + 1;
          to_month = "03";
        } else {
          from_year = selectedFYStartYear + 1;
          from_month = "01";
          to_year = selectedFYStartYear + 1;
          to_month = "03";
        }
        break;

      default:
        break;
    }
  }

  const payload = {
    from_year,
    from_month,
    to_year,
    to_month,
    report_type: selectedReportType,
    mnfUnit: "",
  };

  switch (activeTab) {
    case "0":
      dispatch(getOperatingPerformanceByUnit(payload));
      break;
    case "1":
      dispatch(getNotesToOperatingByUnit(payload));
      break;
    case "2":
      dispatch(getOperatingPersonalPaymentByUnit(payload));
      break;
    case "3":
      dispatch(getOperatingMiscExpensesByUnit(payload));
      break;
    default:
      break;
  }
}, [
  dispatch,
  activeTab,
  selectedFYStartYear,
  selectedQuarter,
  selectedReportType,
]);

  /* ================= DATA SWITCH ================= */
  const data = useMemo(() => {
    let selectedData = [];

    switch (activeTab) {
      case "0":
        selectedData = operatingPerformanceByUnit || [];
        break;
      case "1":
        selectedData = notesToOperatingByUnit || [];
        break;
      case "2":
        selectedData = operatingPersonalPaymentByUnit || [];
        break;
      case "3":
        selectedData = operatingMiscExpensesByUnit || [];
        break;
      default:
        selectedData = [];
    }

    // ✅ COMMON FILTER APPLIED HERE
    return filterRemovedDescriptions(selectedData);
  }, [
    activeTab,
    operatingPerformanceByUnit,
    notesToOperatingByUnit,
    operatingPersonalPaymentByUnit,
    operatingMiscExpensesByUnit,
  ]);

  /* ================= MONTH KEYS ================= */
  const monthKeys = useMemo(() => {
    if (!data.length) return [];
    return Object.keys(data[0]).filter(
      (key) =>
        key.includes("-") &&
        !key.includes("formatted") &&
        key !== "HEAD_ID" &&
        key !== "ACTUAL_LAST_YEAR"
    );
  }, [data]);

  /* ================= COLUMNS ================= */
  const columns = useMemo(() => {
    if (!data.length) return [];

    const baseColumns = [
      {
        title: "Description",
        dataIndex: "DESCRIPTION",
        key: "DESCRIPTION",
        fixed: "left",
        width: 250,
      },

      ...monthKeys.map((key) => ({
        title: key.replace("_", "-"),
        dataIndex: key,
        key,
        align: "right",
        width: 130,

        render: (val, record) => {
          // ✅ Heading row → blank space
          if (String(record?.isHeading) === "1") return "\u00A0";

          if (isPercentage(val)) return val;

          const numericValue = getNumericValue(val);

          return numericValue.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      })),
    ];

    // ✅ Upto → remove total column
    // G...');
    

    // ✅ Add Total column otherwise
    return [
      ...baseColumns,
      {
        title: "Total",
        key: "row_total",
        align: "right",
        width: 160,
        fixed: "right",

        render: (_, record) => {
          // ✅ Heading row → blank
          if (String(record?.isHeading) === "1") return "\u00A0";

          const total = monthKeys.reduce((sum, key) => {
            const value = record[key];
            if (isPercentage(value)) return sum;
            return sum + getNumericValue(value);
          }, 0);

          return total.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      },
    ];
  }, [data, monthKeys, selectedReportType]);

  /* ================= TABLE DATA ================= */
  const tableData = useMemo(() => {
    if (!data.length) return [];

    const formattedData = data.map((item, index) => ({
      ...item,
      key: index,
    }));

    return [...formattedData];
  }, [data, monthKeys]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="loader w-10 h-10 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-5">
      {/* ================= HEADER ================= */}
      <div className="flex gap-3 items-center mb-4">
        <img src={ActiveProject} alt="region" />
        <div>
          <div className="text-[24px] font-semibold">
            Performance Plan - Operating Report
          </div>
          <div className="text-gray-500 text-sm">
            * All figures are in Rupees (in lakhs)
          </div>
        </div>
      </div>

      {/* ================= TABS ================= */}
      <CustomTab
        tabItems={tabData}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {/* ================= FILTER SECTION ================= */}
      <div className="mt-6 bg-white shadow-lg rounded-xl p-4">
        <div className="flex flex-wrap justify-end gap-4 mb-5">
          {/* Financial Year */}
          <div>
            <Select
              className="w-[160px]"
              value={selectedFY}
              onChange={(val) => {
                setSelectedFY(val);
                setSelectedQuarter(null);
              }}
              options={[
                { label: "2023-2024", value: "2023-2024" },
                { label: "2024-2025", value: "2024-2025" },
                { label: "2025-2026", value: "2025-2026" },
                { label: "2026-2027", value: "2026-2027" },
                { label: "2027-2028", value: "2027-2028" },
              ]}
            />
          </div>

          {/* Quarter */}
          <div>
            <Select
              className="w-[200px]"
              value={selectedQuarter}
              onChange={setSelectedQuarter}
              options={quarterOptions}
              allowClear
              placeholder="Select Quarter"
            />
          </div>

          {/* Report Type */}
          <div>
            <Select
              className="w-[120px]"
              value={selectedReportType}
              onChange={setSelectedReportType}
              options={reportTypeOptions}
            />
          </div>
        </div>

        {/* ================= TABLE ================= */}
        <ConsolidatedReportsTable
          columns={columns}
          dataSource={tableData}
          scroll={{ x: 1800 }}
          rowClassName={(record) =>
            record.key === "grand_total" ? "font-bold bg-gray-100" : ""
          }
        />
      </div>
    </div>
  );
};

export default OperatingPerformanceQuarterWiseReport;